import React from 'react';
import { Platform, Text, TouchableOpacity, View } from 'react-native';
import {
  HelperText,
  IconButton,
  TextInput,
} from 'react-native-paper';
import { Picker } from '@react-native-picker/picker';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import ProductImagePicker from './ProductImagePicker';
import { iconSize } from '../../../../utils/libs';
import {
  PARENT_VARIANT_THUMBNAIL_CAMERA_ID,
  SIZE_OPTIONS,
  UNIT_OF_MASS_OPTIONS,
  type EditableVariant,
  type ProductFormData,
} from '../productFormTypes';
import { useProductFormFieldTheme } from './productFormStyles';

/**
 * Full-presentation product fields extracted verbatim from NewProduct.tsx
 * (US3, T053): image section, mass/unit, price-type selector, and the
 * Additional Information accordion. Both NewProduct and the wizard's
 * More-options expansion render these exact components.
 */

export interface ProductImageSectionProps {
  previewUri: string;
  onUploaded: (b64: string, local?: string) => void;
  onDiscard: () => void;
  onProcessingChange: (processing: boolean) => void;
  /** Web renders a collapsible accordion; native a plain section. */
  expanded: boolean;
  onToggleExpanded: () => void;
}

export const ProductImageSection: React.FC<ProductImageSectionProps> = ({
  previewUri,
  onUploaded,
  onDiscard,
  onProcessingChange,
  expanded,
  onToggleExpanded,
}) => {
  const { theme, styles } = useProductFormFieldTheme();

  if (Platform.OS === 'web') {
    return (
      <>
        <TouchableOpacity
          onPress={onToggleExpanded}
          style={styles.accordionHeader}>
          <Text style={[styles.accordionTitle, { flex: 1 }]}>
            Product Image
          </Text>
          <IconButton
            icon="chevron-down"
            size={iconSize(20)}
            iconColor={theme.colors.text}
            style={[
              styles.accordionChevron,
              {
                transform: [{ rotate: expanded ? '180deg' : '0deg' }],
              },
            ]}
          />
        </TouchableOpacity>
        {expanded ? (
          <ProductImagePicker
            previewUri={previewUri}
            onUploaded={onUploaded}
            onDiscard={onDiscard}
            onProcessingChange={onProcessingChange}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      <Text style={styles.sectionTitle}>Product Image</Text>
      <ProductImagePicker
        previewUri={previewUri}
        onUploaded={onUploaded}
        onDiscard={onDiscard}
        onProcessingChange={onProcessingChange}
      />
    </>
  );
};

export interface MassAndUnitFieldsProps {
  formData: Pick<ProductFormData, 'mass' | 'unitofmass'>;
  priceType: string;
  onChangeField: (field: string, value: string) => void;
  /** Deterministic visual focus for non-interactive renderers such as Remotion. */
  focusedField?: 'mass' | 'unit';
}

// Display grouping for the unit chips. Every UNIT_OF_MASS_OPTIONS unit is
// listed (the empty placeholder option is not a unit); the groups only decide
// the order the units appear in.
const UNIT_OF_MASS_GROUPS = [
  { label: 'Weight', values: ['Kg', 'g', 'Mg'] },
  { label: 'Volume', values: ['Lt', 'Ml'] },
  { label: 'Length', values: ['M', 'Cm', 'Mm', 'Ft', 'inch'] },
].map(group => ({
  label: group.label,
  units: group.values.flatMap(value =>
    UNIT_OF_MASS_OPTIONS.filter(opt => opt.value === value),
  ),
}));

export const MassAndUnitFields: React.FC<MassAndUnitFieldsProps> = ({
  formData,
  priceType,
  onChangeField,
}) => {
  const { styles } = useProductFormFieldTheme();

  // UI optimization (create forms): for sell-per-item products, mass/volume and
  // unit-of-mass inputs are no longer shown. Users include size in the product
  // name (e.g. "Coke 500ml"). Create payloads send empty strings for both
  // fields — API shape unchanged. Edit screens still show these fields when an
  // existing product already has mass/unit data (legacy support).
  if (priceType === 'item') {
    // Previously rendered Mass/Volume TextInput + unit picker here.
    // Kept commented for easy restore if we revert this UX decision:
    // return (
    //   <>
    //     <TextInput
    //       mode="outlined"
    //       label="Mass/Volume"
    //       placeholder="Mass/Volume i.e 250"
    //       value={formData.mass}
    //       onChangeText={t => onChangeField('mass', t.replace(/[^0-9.]/g, ''))}
    //       keyboardType="numeric"
    //       placeholderTextColor={theme.colors.textSecondary}
    //       {...inputOutlineProps}
    //       outlineColor={
    //         focusedField === 'mass'
    //           ? theme.colors.primary
    //           : inputOutlineProps.outlineColor
    //       }
    //       {...webPlaceholderTheme}
    //       style={styles.input}
    //     />
    //     {unitPicker}
    //   </>
    // );
    return null;
  }

  // Sell-per-unit: unit-of-mass chips only (mass number not collected on create).
  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>Unit you sell in</Text>
      {UNIT_OF_MASS_GROUPS.map(group => (
        <View key={group.label} style={styles.unitGroupRow}>
          <Text style={styles.unitGroupLabel}>{group.label}</Text>
          <View style={styles.chipWrap}>
            {group.units.map(unit => {
              const selected = formData.unitofmass === unit.value;
              return (
                <TouchableOpacity
                  key={unit.value}
                  style={[
                    styles.choiceChip,
                    selected ? styles.choiceChipSelected : null,
                  ]}
                  onPress={() => onChangeField('unitofmass', unit.value)}
                  accessibilityRole="radio"
                  accessibilityState={{ selected }}
                  accessibilityLabel={unit.label}>
                  <Text
                    style={[
                      styles.choiceChipText,
                      selected ? styles.choiceChipTextSelected : null,
                    ]}>
                    {unit.value}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      ))}
    </View>
  );
};

export interface PriceTypeTilesProps {
  priceType: string;
  onChangePriceType: (priceType: string) => void;
  disabled?: boolean;
}

// The two sell types as tiles, the same look as BackOfficeAddProductScreen.
const PRICE_TYPE_OPTIONS: Array<{
  value: 'item' | 'unit';
  label: string;
  hint: string;
}> = [
  {
    value: 'item',
    label: 'Sell Per Item',
    hint: 'like 1 bottle or 1 pack',
  },
  {
    value: 'unit',
    label: 'Sell Per Unit',
    hint: 'like 1 Kg of rice',
  },
];

export const PriceTypeTiles: React.FC<PriceTypeTilesProps> = ({
  priceType,
  onChangePriceType,
  disabled = false,
}) => {
  const { styles } = useProductFormFieldTheme();

  return (
    <>
      <View style={[styles.sectionHeader, styles.groupStart]}>
        <Text style={styles.sectionTitle}>How will you sell this product?</Text>
      </View>
      <View style={styles.priceTypeTiles}>
        {PRICE_TYPE_OPTIONS.map(option => {
          const selected = priceType === option.value;
          return (
            <TouchableOpacity
              key={option.value}
              style={[
                styles.priceTypeTile,
                selected ? styles.priceTypeTileSelected : null,
                disabled ? { opacity: 0.55 } : null,
              ]}
              activeOpacity={0.7}
              disabled={disabled}
              onPress={() => onChangePriceType(option.value)}
              accessibilityRole="radio"
              accessibilityState={{ selected, disabled }}>
              <View style={styles.priceTypeTileHeader}>
                <View
                  style={[
                    styles.priceTypeRadio,
                    selected ? styles.priceTypeRadioSelected : null,
                  ]}
                />
                <Text style={styles.priceTypeTileLabel}>{option.label}</Text>
              </View>
              <Text style={styles.priceTypeTileHint}>{option.hint}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </>
  );
};

export interface AdditionalInformationSectionProps {
  formData: Pick<
    ProductFormData,
    'barcode' | 'size' | 'model' | 'bp_product_image_uri'
  >;
  hasVariants: boolean;
  priceType: string;
  activeVariantRows: EditableVariant[];
  onChangeField: (field: string, value: string) => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  onScanRootBarcode: () => void;
  /** Variant-parent thumbnail wiring (NewProduct behavior). */
  activeVariantCameraId: string | null;
  setActiveVariantCameraId: (id: string | null) => void;
  onProductImageUploaded: (b64: string, local?: string) => void;
  onDiscardProductImage: () => void;
  onImageProcessingChange: (processing: boolean) => void;
}

export const AdditionalInformationSection: React.FC<
  AdditionalInformationSectionProps
> = ({
  formData,
  hasVariants,
  priceType,
  activeVariantRows,
  onChangeField,
  expanded,
  onToggleExpanded,
  onScanRootBarcode,
  activeVariantCameraId,
  setActiveVariantCameraId,
  onProductImageUploaded,
  onDiscardProductImage,
  onImageProcessingChange,
}) => {
  const {
    theme,
    styles,
    inputOutlineProps,
    webPlaceholderTheme,
    pickerItemColor,
    pickerItemColorSecondary,
  } = useProductFormFieldTheme();

  const hint = hasVariants
    ? 'Product photo and model · Optional'
    : priceType === 'item'
      ? 'Photo, barcode, size and model · Optional'
      : 'Photo, barcode and model · Optional';

  // A fragment, so the host card's gap spaces the header and the body.
  return (
    <>
      <TouchableOpacity
        onPress={onToggleExpanded}
        style={styles.accordionHeader}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        testID="additional-information-toggle">
        <View style={styles.accordionHeaderBody}>
          <Text style={styles.sectionTitle}>Additional Information</Text>
          <Text style={styles.sectionHint}>{hint}</Text>
        </View>
        <MaterialCommunityIcons
          name="chevron-down"
          size={iconSize(22)}
          color={theme.colors.textGray500}
          style={{
            transform: [{ rotate: expanded ? '180deg' : '0deg' }],
          }}
        />
      </TouchableOpacity>

      {expanded ? (
        <View style={styles.accordionBody}>
          {hasVariants ? (
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>
                Product Thumbnail{' '}
                <Text style={styles.fieldLabelOptional}>· Optional</Text>
              </Text>
              <Text style={styles.fieldHint}>
                Falls back to the first variant image if not set
              </Text>
              <ProductImagePicker
                previewUri={formData.bp_product_image_uri}
                cameraActive={
                  activeVariantCameraId === PARENT_VARIANT_THUMBNAIL_CAMERA_ID
                }
                autoOpenWhenEmpty={
                  activeVariantCameraId === PARENT_VARIANT_THUMBNAIL_CAMERA_ID
                }
                onRequestCameraActivation={() =>
                  setActiveVariantCameraId(PARENT_VARIANT_THUMBNAIL_CAMERA_ID)
                }
                onUploaded={(b64: string, local?: string) => {
                  onProductImageUploaded(b64, local);
                  setActiveVariantCameraId(null);
                }}
                onDiscard={() => {
                  onDiscardProductImage();
                  setActiveVariantCameraId(null);
                }}
                onProcessingChange={onImageProcessingChange}
              />
            </View>
          ) : (
            <View style={styles.field}>
              <Text style={styles.fieldLabel}>
                Product Image{' '}
                <Text style={styles.fieldLabelOptional}>· Optional</Text>
              </Text>
              <ProductImagePicker
                previewUri={formData.bp_product_image_uri}
                onUploaded={onProductImageUploaded}
                onDiscard={onDiscardProductImage}
                onProcessingChange={onImageProcessingChange}
              />
            </View>
          )}
          {!hasVariants ? (
            <View style={styles.field}>
              <TextInput
                mode="outlined"
                label="Barcode (optional)"
                value={formData.barcode}
                onChangeText={t => onChangeField('barcode', t)}
                placeholderTextColor={theme.colors.textSecondary}
                {...inputOutlineProps}
                {...webPlaceholderTheme}
                style={styles.input}
                right={
                  <TextInput.Icon
                    icon="barcode-scan"
                    color={theme.colors.primaryDark}
                    onPress={onScanRootBarcode}
                  />
                }
              />
              {formData.barcode ? (
                <HelperText
                  type={
                    formData.barcode.trim().length >= 8 &&
                    /^[A-Za-z0-9]+$/.test(formData.barcode.trim())
                      ? 'info'
                      : 'error'
                  }
                  visible>
                  {formData.barcode.trim().length >= 8 &&
                  /^[A-Za-z0-9]+$/.test(formData.barcode.trim())
                    ? null
                    : 'Alphanumeric only, at least 8 characters'}
                </HelperText>
              ) : null}
            </View>
          ) : (
            <View style={styles.variantBarcodeList}>
              <Text style={styles.variantBarcodeListTitle}>
                Variant Barcodes
              </Text>
              {activeVariantRows.map((variant, index) => (
                <Text
                  key={String(variant._id || variant._localId || index)}
                  style={styles.variantBarcodeListItem}>
                  {`${variant.bp_variant_display_name || `Variant ${index + 1}`}: ${String(variant.bp_variant_barcode || 'No barcode')}`}
                </Text>
              ))}
            </View>
          )}

          {!hasVariants && priceType === 'item' ? (
            <View style={styles.field}>
              <View style={styles.pickerContainer}>
                <Picker
                  selectedValue={formData.size}
                  onValueChange={value => onChangeField('size', String(value))}
                  dropdownIconColor={theme.colors.primaryDark}
                  mode="dropdown"
                  style={styles.picker}>
                  <Picker.Item
                    label="Size"
                    value=""
                    color={pickerItemColorSecondary}
                  />
                  {/* "Size" above is the empty choice, as in the back office. */}
                  {SIZE_OPTIONS.filter(opt => opt.value).map(opt => (
                    <Picker.Item
                      key={opt.value}
                      label={opt.label}
                      value={opt.value}
                      color={pickerItemColor}
                    />
                  ))}
                </Picker>
              </View>
            </View>
          ) : null}

          <View style={styles.field}>
            <TextInput
              mode="outlined"
              label="Model | Type | Make | Other"
              value={formData.model}
              onChangeText={t => onChangeField('model', t)}
              placeholderTextColor={theme.colors.textSecondary}
              {...inputOutlineProps}
              {...webPlaceholderTheme}
              style={styles.input}
            />
          </View>
        </View>
      ) : null}
    </>
  );
};
